// #region imports
import {
  Actions,
  ExportPageViaEmail2,
  Share,
} from '../../../components/Actions'
import Description from '../../../components/Description'
import Headline from '../../../components/Headline'
import SiblingsMenu from '../../../components/SiblingsMenu'
import SignoutTextBox from '../../../components/SignoutTextBox'
import Banner from '../../../components/ui/Banner'
import IAM from '../../../layouts/economic-impact-assessment-module/page'
import MainLayout from '../../../layouts/main'
import { useClient, usePage } from '../../../utils/context'
import fetchToggleOptions from '../../../utils/fetchToggleOptions'
import filterEntities from '../../../utils/filterEntities'
import getActiveToggle from '../../../utils/getActiveToggle'
import useHasAccess from '../../../utils/hooks/useHasAccess'
// Modern session resolution (legacy AES path removed Phase 4)
import PreSigninBlock from '@/components/PreSigninBlock'
import ErrorPage from '@/pages/_error'
import { serialize } from 'cookie'
import getConfig from 'next/config'
import {
  EntityContainer,
  MainTitle,
  TitleContainer,
  _SubTitle,
} from '../../../styles/MainContentStyles'
import { SSRRedirectSafe } from '../../../utils'
import { devLog } from '../../../utils/log'
import { hideSecuredPages } from '../../../utils/security/hideSecuredPages'
import { resolveSession } from '../../../utils/security/legacyAdapter'
import useEntityText from '../../../utils/useEntityText'
import { CryptoHelpers } from '../../api/crypto/components/CryptoHelpers'

const { serverRuntimeConfig } = getConfig();
var codecLzma = require('json-url')('lzma')
var codecLzString = require('json-url')('lzstring')

// #endregion

const PageTemplate = (): JSX.Element => {
  const {
    filterToggles,
    pageData: { parentPageID },
    filters: {
      decodedPayload: { WebID, project },
    },
    isvalidated,
  } = usePage()

  const { clientLongName, clientID, clientAlias } = useClient()
  const SubTitle = () => <_SubTitle>{useEntityText('SubTitle')}</_SubTitle>
  const { authorised, session } = useHasAccess()
  const enableNewSignInOut = useClient().enableNewSignInOut

  let AuthorisedToView = isvalidated
  if (!AuthorisedToView) {
    AuthorisedToView = authorised
  }

  const AreaList = filterToggles
    .find(({ key }) => key === 'WebID')
    .list.map(({ Label, Value }) => ({
      label: Label,
      value: Value,
    }))

  const areaInitialState = AreaList.find(({ value }) => +value === WebID)

  const Header = () => {
    return (
      <EntityContainer>
        {AuthorisedToView && (
          <>
            <TitleContainer>
              <MainTitle>{areaInitialState.label || clientLongName}</MainTitle>
              <SubTitle />
            </TitleContainer>
            <Actions>
              {!enableNewSignInOut && session && <SignoutTextBox />}
              <Share />
              <ExportPageViaEmail2 reportname={project} />
            </Actions>
          </>
        )}
      </EntityContainer>
    )
  }

  return (
    <MainLayout>
      {parentPageID > 0 && <SiblingsMenu />}
      <Header />
      <Banner />
      {AuthorisedToView ? (
        <>
          <Headline />
          <Description />
          <IAM />
        </>
      ) : (
        session ? (<ErrorPage statusCode={401} />) : (<PreSigninBlock />)
      )}
    </MainLayout>
  )
}

const PageComponent = ({ client, page }): JSX.Element => {
  if (!page || !client) {
    return <ErrorPage statusCode={404} />;
  }
  return <PageTemplate />;
};

PageComponent.getInitialProps = async function ({
  query,
  req,
  res,
}): Promise<{}> {



  const { cosmosdb, cookies } = req
  const { clientAlias, ...providedFilters } = query
  const handle = 'economic-impact-assessment-module'
  const fourOfour = { props: { client: null, page: null } }
  const client_collection = cosmosdb.collection('Clients')
  const appSettings_collection = cosmosdb.collection('AppSettings')
  const client: any = await client_collection.findOne({ clientAlias })
  const appSettings = await appSettings_collection.findOne({ _id: 1 })
  // no client? => 404
  if (!client) return fourOfour

  // client but not a page => 404
  const { clientID, access, clientLongName, prefix, pages, isDisabled, siteSecured } = client
  if (isDisabled) return SSRRedirectSafe(res, clientAlias, 'find-out-more')

  // no page? => 404
  const currentPage = await pages.find(({ pageAlias }) => pageAlias === handle)
  if (!currentPage) return fourOfour

  const securityResult = await hideSecuredPages({
    req,
    res,
    query,
    client,
    clientAlias: clientAlias as string,
    currentPage,
    pages,
  });

  if (securityResult.redirect) {
    return securityResult.redirect;
  }

  const { filteredPages, enableNewSignInOut, enableModalSignInUp, sanitizedSession, accessResult } = securityResult;

  const { getServerSideBooleanFlag } = await import('../../../lib/configcat/serverSide');
  const { FEATURE_FLAGS } = await import('../../../utils/featureFlags');
  const showContactInfo = await getServerSideBooleanFlag(
    FEATURE_FLAGS.SHOW_EIAM_CONTACT_INFO,
    false,
    { clientAlias: clientAlias as string },
    { cookies, configKey: 'economic', route: { clientAlias: clientAlias as string, topicAlias: handle } }
  );


  // Passwordless Section


  let queryParam: string = query.param
  let validate = true
  if (queryParam) {
    //const dec = CryptoHelpers.decryptForce(queryParam.replaceAll(" ", "+"))
    const dec = CryptoHelpers.decryptWithPrivateKeyForce(queryParam.replaceAll(" ", "+"))
    const params = JSON.parse(dec)
    if (new Date().toJSON().slice(0, 10).replace(/-/g, '/') != params.today) {
      validate = false
    }
    if (params.username != client.login.username) {
      validate = false
    }
    if (params.password != client.login.password) {
      validate = false
    }

  } else {
    validate = false
  }

  const isvalidated: boolean = validate


  // Legacy token (crypto-js) may be present; if validated we do not re-issue here (API / auth handles new issuance)
  const legacyOrNewToken = cookies[`eco-auth.csrf-token-${clientID}`];
  const { session: resolvedSession } = await resolveSession(legacyOrNewToken, clientID);
  const session = { token: resolvedSession };

  // console.log(session)
  const isLite = access != 'full'
  const layoutData = await import(
    `../../../layouts/economic-impact-assessment-module`
  );
  const { fetchData, pageContent, activeCustomToggles } = layoutData
  const pageData = currentPage
  const pageDefaultFilters = (pageContent['filterToggles'] || []).reduce(
    (acc, { ParamName, DefaultValue }) => ({
      ...acc,
      [ParamName]: DefaultValue,
    }),
    { clientID, IsLite: isLite, clientAlias, clientLongName },
  )




  /**
   * the provided filters from the query string can be wrong
   * but we don't know it yet at this stage and we'll have to update them later
   * (see note in the fetchToggleOptions() method)
   */
  const tempfilters = {
    ...pageDefaultFilters,
    ...providedFilters,
  }

  const payload = {
    ClientID: clientID,
    WebID: 10,
    construction: {},
    industries: [],
  }

  let decodedPayload;
  let compressedPayload;

  // Clear IAM cookies ONLY if:
  // 1. User navigated directly to the page (no payload in URL)
  // 2. AND cookies exist from a previous session
  // 3. AND this is NOT an export request (pdfmyurl.com uses export_payload)
  // 4. AND cookies are old (not just set within last 10 seconds)
  const hasIAMCookies = !!(cookies['iampl'] || cookies['iampn']);
  const isDirectNavigation = !providedFilters.export_payload && !providedFilters.iampl;
  const isExportRequest = !!providedFilters.xp; // Export pages add xp=1 parameter

  // Check if cookies are fresh (set within last 10 seconds)
  const cookieTimestamp = cookies['iamts'] ? parseInt(cookies['iamts'], 10) : 0;
  const cookieAge = Date.now() - cookieTimestamp;
  const areCookiesFresh = cookieAge < 10000; // 10 seconds

  devLog('🕒 [IAM SSR] Cookie age check:', {
    hasIAMCookies,
    isDirectNavigation,
    isExportRequest,
    cookieAge: Math.round(cookieAge / 1000) + 's',
    areCookiesFresh,
  });

  // Track if we're clearing cookies so we don't use them
  let cookiesCleared = false;

  if (hasIAMCookies && isDirectNavigation && !isExportRequest && !areCookiesFresh) {
    devLog('🧹 [IAM SSR] Direct navigation detected with stale cookies - clearing them');
    // Clear the cookies by setting them with expired date
    const clearCookieOptions = {
      path: '/',
      maxAge: -1, // Expire immediately
      httpOnly: false,
      secure: process.env.NODE_ENV === 'production',
      sameSite: 'lax' as const,
    };

    res.setHeader('Set-Cookie', [
      serialize('iampl', '', clearCookieOptions),
      serialize('iampn', '', clearCookieOptions),
      serialize('iamts', '', clearCookieOptions),
    ]);

    cookiesCleared = true;
    devLog('✅ [IAM SSR] Stale cookies cleared');
  } else if (areCookiesFresh) {
    devLog('✨ [IAM SSR] Cookies are fresh (just set), keeping them');
  }

  // Priority 1: Check for export_payload (direct payload for PDF/Word export)
  // Priority 2: Check cookies (POST method) - ONLY if not just cleared
  // Priority 3: Fall back to query params (legacy GET method)
  devLog('🔍 [IAM SSR] Checking for data source...', {
    hasExportPayload: !!providedFilters.export_payload,
    hasCookie: !!cookies['iampl'] && !cookiesCleared,
    hasQueryParam: !!providedFilters.iampl,
    cookiesCleared,
  });

  if (providedFilters.export_payload) {
    // Direct payload for export (pdfmyurl.com, Word export)
    devLog('📦 [IAM SSR] Export payload detected (direct from URL)');
    compressedPayload = providedFilters.export_payload;
  } else if (!cookiesCleared) {
    // Check cookies (POST method) or query params (legacy GET method)
    // Don't use cookies if we just cleared them!
    const compressedPayloadFromCookie = cookies['iampl'];
    compressedPayload = compressedPayloadFromCookie || providedFilters.iampl;
    devLog('📦 [IAM SSR] Using cookie/query param data', {
      source: compressedPayloadFromCookie ? 'cookie' : (providedFilters.iampl ? 'query' : 'none'),
      payloadLength: compressedPayload?.length,
    });
  } else {
    // Cookies were cleared, fall back to query params only
    compressedPayload = providedFilters.iampl;
    devLog('📦 [IAM SSR] Cookies cleared, using query param data only', {
      source: providedFilters.iampl ? 'query' : 'none',
      payloadLength: compressedPayload?.length,
    });
  }

  // Decompress the payload if we have one
  if (compressedPayload) {
    try {
      // Try lzstring decompression first (matches client-side priority)
      decodedPayload = await codecLzString.decompress(compressedPayload);
      devLog('✅ [IAM SSR] Decompressed payload successfully (lzstring)', {
        hasConstructionData: !!decodedPayload.construction,
        industriesCount: decodedPayload.industries?.length,
        WebID: decodedPayload.WebID,
      });
    } catch (lzStringError) {
      try {
        // Fallback to LZMA decompression
        decodedPayload = await codecLzma.decompress(compressedPayload);
        devLog('✅ [IAM SSR] Decompressed payload successfully (lzma)');
      } catch (lzmaError) {
        try {
          // Final fallback: try base64 decoding
          const decodedString = atob(compressedPayload);
          decodedPayload = JSON.parse(decodedString);
          devLog('✅ [IAM SSR] Decompressed payload successfully (base64)');
        } catch (base64Error) {
          console.error('❌ [IAM SSR] All decompression methods failed:', { lzStringError, lzmaError, base64Error });
          decodedPayload = payload;
        }
      }
    }
  } else {
    devLog('ℹ️ [IAM SSR] No compressed payload, using default empty state');
    decodedPayload = payload;
  }

  const filterToggles = await fetchToggleOptions(tempfilters, pageContent['filterToggles'] ? [...pageContent['filterToggles']] : [],)

  const activeFilters = filterToggles.map(({ key, value }) => ({
    [key]: value,
  }))

  // let's update the filters with the active one
  const filters = Object.assign(tempfilters, ...activeFilters)

  const contentData = await fetchData({
    filters,
    decodedPayload: { ...decodedPayload },
  })

  // we pass that data to interpolate the entities
  const customToggles = await activeCustomToggles({ filterToggles })
  const currentAreaName =
    getActiveToggle(filterToggles, 'WebID') || clientLongName
  const HasPrefix = client.prefix === 'the'
  const prefixedAreaName = `${prefix} ${currentAreaName}`
  const data = {
    HasPrefix,
    currentAreaName,
    prefixedAreaName,
    ...customToggles,
  }

  /*
   * There is a bug in stored proc where the quantity
   * of selected sectors multiples the total of all rows in the column.
   * Please read associated trello cards for more information
   */
  if (decodedPayload.industries.length > 1) {
    for (let i = 0; i < contentData.EmploymentData.length; i++) {
      if (contentData.EmploymentData[i].IndustryType === 'Other') {
        contentData.EmploymentData[i].NJobs = contentData.EmploymentData[i].NJobs / decodedPayload.industries.length
      }
    }

    for (let i = 0; i < contentData.ChangeValueAddData.length; i++) {
      if (contentData.ChangeValueAddData[i].IndustryType === 'Other') {
        contentData.ChangeValueAddData[i].NLocalValueAdd = contentData.ChangeValueAddData[i].NLocalValueAdd / decodedPayload.industries.length
      }
    }
  }

  const entities = await filterEntities(filters, pageContent['entities'], {
    contentData,
    data,
  })



  const page = {
    handle,
    contentData,
    filters: { ...filters, decodedPayload },
    filterToggles,
    providedFilters,
    pageData,
    entities,
    entityData: data,
    isvalidated,
    // no direct token; session handled separately
  }

  return {
    appSettings,
    session: sanitizedSession,
    client: { ...client, pages: filteredPages, enableNewSignInOut, enableModalSignInUp, isCouncilUser: true, councilClientAlias: clientAlias as string, showContactInfo },
    page,
  }
}

export default PageComponent
